import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../config/cta'
import LoopCTAs from '../../../components/LoopCTAs'
import { FadeIn } from '../../../components/Motion'
import Seo from '../../../components/Seo'

const exampleQuestions = [
  'Tell me about your experience as a member. What originally drew you to join?',
  'Think about the value you get from your membership. How well does it meet your expectations?',
  'How would you describe your level of engagement with our programs and events?',
  'When your membership comes up for renewal, what factors influence your decision?',
  'What would you tell a colleague who was considering joining our organization?',
]

const useCases = [
  { title: 'Member Satisfaction', description: 'Overall membership experience and value' },
  { title: 'Engagement Insights', description: 'Program and event participation drivers' },
  { title: 'Renewal Research', description: 'Understanding retention and churn factors' },
  { title: 'New Member Onboarding', description: 'First-year experience and integration' },
  { title: 'Benefits Evaluation', description: 'Value perception of member benefits' },
  { title: 'Community Feedback', description: 'Peer networking and connection quality' },
]

export default function Membership() {
  return (
    <div>
      <Seo
        title="Membership Organization Feedback & Retention Research"
        description="Understand what drives member engagement and what causes members to lapse. PrismScope helps membership organizations improve experience, benefits, and renewal rates."
        path="/industries/nonprofit/membership"
      />
      {/* Breadcrumbs */}
      <div className="bg-white border-b-2 border-intelligence-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries" className="hover:text-intelligence-900">Industries</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/industries/nonprofit" className="hover:text-intelligence-900">Nonprofit</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Membership</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Industry · Nonprofit
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.98]">
              Membership Organization Feedback
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-3xl mx-auto mb-10 leading-relaxed">
              Understand what members really think about their experience—from
              initial joining to ongoing engagement to renewal decisions.
            </p>
            <div className="relative inline-block group">
              <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-[#00008B] translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2 group-hover:translate-y-2" />
              <a href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1" className="relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-intelligence-900 border-2 border-intelligence-900 rounded-lg">
                {SECONDARY_CTA_TEXT} <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The Challenge */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">The challenge</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">The Challenge</h2>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div aria-hidden="true" className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2" />
              <div className="relative bg-white border-2 border-intelligence-900 rounded-2xl p-8 md:p-10">
                <p className="text-lg text-intelligence-700 mb-6">
                  Membership organizations need engaged members to thrive, but understanding
                  what drives engagement—and what causes members to lapse—is difficult.
                </p>
                <p className="text-lg text-intelligence-700 mb-6">
                  Renewal rates don&apos;t tell the full story:
                </p>
                <ul className="space-y-2 text-intelligence-600 mb-6">
                  <li>• Why members become disengaged before they leave</li>
                  <li>• What benefits matter most vs. least</li>
                  <li>• How members perceive value relative to dues</li>
                  <li>• Where the member experience falls short</li>
                </ul>
                <p className="text-lg text-intelligence-900 font-medium text-center">
                  PrismScope helps membership organizations understand and improve member experience.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">Use cases</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Use Cases</h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-5">
            {useCases.map((uc, i) => {
              const colors = ['#3b82f6', '#8b5cf6', '#00008B']
              const color = colors[i % 3]
              return (
                <FadeIn key={uc.title} delay={i * 0.04}>
                  <div className="relative h-full">
                    <div aria-hidden="true" className="absolute inset-0 rounded-xl translate-x-1 translate-y-1" style={{ backgroundColor: color }} />
                    <div className="relative bg-white rounded-xl border-2 border-intelligence-900 p-5 h-full">
                      <h3 className="font-bold text-intelligence-900 mb-1">{uc.title}</h3>
                      <p className="text-intelligence-600 text-sm">{uc.description}</p>
                    </div>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* Example Questions */}
      <section className="bg-intelligence-100 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">What we&apos;d ask</p>
              <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900">Example Questions</h2>
            </div>
          </FadeIn>
          <div className="space-y-3">
            {exampleQuestions.map((q, i) => (
              <FadeIn key={i} delay={i * 0.04}>
                <div className="bg-white rounded-lg border-2 border-intelligence-900 p-5 shadow-[3px_3px_0_0_rgba(15,23,42,1)]">
                  <p className="text-intelligence-700 italic">&ldquo;{q}&rdquo;</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-intelligence-900 py-20 md:py-24 relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B]" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-[#00008B] via-[#8b5cf6] to-[#3b82f6]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-400 mb-3">Your move</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Increase member engagement and retention</h2>
            <p className="text-lg text-intelligence-400 mb-10">Run your first campaign. Try a 3-minute conversation first.</p>
            <LoopCTAs location="industry-nonprofit-membership-final" align="center" />
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
